'use client'

import { useRef, useState } from 'react'

import CheckEnterRoomScreen from '@/app/[locale]/[roomId]/components/CheckEnterRoomScreen'
import IframeContent from '@/app/[locale]/[roomId]/components/IframeContent'
import TrailerCarousel from '@/app/[locale]/[roomId]/components/TrailerCarousel'
import TrailerThumbnails from '@/app/[locale]/[roomId]/components/TrailerThumbnails'
import WorldSplash from '@/app/[locale]/[roomId]/components/WorldSplash'
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext'
import { TrailerViewMediaProvider } from '@/context/TrailerViewMediaContext'
import { useUAContext } from '@/context/UAContext'
import { useWorldDetailsContext } from '@/context/WorldDetailsContext'
import { useEnableFull3d } from '@/hooks/useEnableFull3d'
import { useFullscreenBodyStyle } from '@/hooks/useFullscreenBodyStyle'
import { useTransparentBackground } from '@/hooks/useTransparentBackground'
import { cn } from '@/lib/utils'

type WorldProps = {
  className?: string
  setScrollLock?: (isLocked: boolean) => void
}

function World({ className, setScrollLock }: WorldProps) {
  const [isShowSplash, setIsShowSplash] = useState(false)
  const worldRootRef = useRef<HTMLDivElement>(null)
  const enableFull3d = useEnableFull3d()
  const { isMobile } = useUAContext()
  const shouldShowMobileTip = !enableFull3d && isMobile
  const { isTrailer } = useCheckEnterRoomContext()
  const { worldMeta } = useWorldDetailsContext()
  const is3DCommerceTransparentBg =
    worldMeta?.scene_settings?.eCommerceConfigs?.is3DEC &&
    worldMeta?.scene_settings?.eCommerceConfigs?.transparentBackground
  const shouldTransparentPageBackground = is3DCommerceTransparentBg && enableFull3d

  useFullscreenBodyStyle()
  useTransparentBackground(shouldTransparentPageBackground)

  return (
    <div
      ref={worldRootRef}
      className={cn('relative w-full overflow-hidden', {
        'rounded-[15px]': !enableFull3d,
        'bg-[rgba(0,0,0,0.6)]': !isTrailer && !isMobile,
        'bg-transparent': is3DCommerceTransparentBg,
      })}
    >
      <div className={cn({ 'h-dvh w-full': enableFull3d }, className)}>
        <CheckEnterRoomScreen>
          {isTrailer || shouldShowMobileTip ? (
            <TrailerViewMediaProvider>
              <TrailerCarousel />
              {!enableFull3d && (
                <div className="relative w-full overflow-hidden">
                  <TrailerThumbnails />
                </div>
              )}
            </TrailerViewMediaProvider>
          ) : (
            <>
              <WorldSplash
                isVisible={isShowSplash}
                className={cn('absolute inset-0 z-20', {
                  'max-md:hidden': !enableFull3d,
                })}
              />
              <IframeContent setIsShowSplash={setIsShowSplash} setScrollLock={setScrollLock} />
            </>
          )}
        </CheckEnterRoomScreen>
      </div>
    </div>
  )
}

export default World
